import { useState, type Dispatch, type SetStateAction } from "react"
import { useTranslation, Trans } from "react-i18next"
import cc from "classnames"

import { logout } from "@stores/auth"

import Image from "@components/Image"
import Link from "@components/Link"
import ProfilePicturePlaceholder from "@components/ProfilePicturePlaceholder"

import { ReactComponent as ChevronDownSmall } from "@icons/chevron-down-small.svg"

interface DropDownLinkProps {
  href?: string
  link_text?: string
  is_logout?: boolean
  setIsDropdownVisible: Dispatch<SetStateAction<boolean>>
}

const DropdownLink = (props: DropDownLinkProps): JSX.Element => {
  const { href, link_text, is_logout, setIsDropdownVisible } = props

  const logoutAndHideDropdown = async () => {
    setIsDropdownVisible(false)
    await logout()
    window.location.href = "/"
  }

  return (
    <li className="relative px-24 py-6">
      {is_logout ? (
        <button className="expand-hitbox text-red" onClick={logoutAndHideDropdown} data-cy="user-logout-button">
          {link_text}
        </button>
      ) : (
        <Link href={href} class_name="expand-hitbox" onClick={() => setIsDropdownVisible(false)}>
          {link_text}
        </Link>
      )}
    </li>
  )
}

interface AccountActionDropdownProps {
  user: Domain.Shared.DataTransferObjects.UserData
  bg_color?: "transparent" | "white" | "spinach"
}

const AccountActionDropdown = (props: AccountActionDropdownProps): JSX.Element => {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)
  const { t } = useTranslation<string>("common")

  const { user } = props

  return (
    <>
      <div className="relative z-50" data-cy="user-dropdown">
        <button
          onClick={() => setIsDropdownVisible(!isDropdownVisible)}
          className={cc("flex h-32 items-center rounded-8 border pl-8 pr-12 text-14", {
            "border-white border-opacity-10": props.bg_color === "spinach",
            "border-neutral-200": props.bg_color === "white",
            "border-vantablack border-opacity-10": props.bg_color === "transparent",
          })}
          data-cy="user-dropdown-button"
        >
          <div className="relative mr-8 h-16 w-16 overflow-hidden rounded-4">
            {user?.media?.permalink ? (
              <div className="aspect-h-1 aspect-w-1 h-16 w-16">
                <Image
                  src={user?.media?.permalink}
                  alt={user?.media?.alt ?? user?.first_name ?? ""}
                  width={16}
                  height={16}
                  className="object-cover"
                />
              </div>
            ) : (
              <ProfilePicturePlaceholder width={16} height={16} user={user} font_size="text-10" />
            )}
          </div>
          <Trans
            i18nKey="common:header.helloUser"
            components={[<strong className="ml-4 font-semibold" data-cy="user-first-name" key={0} />]}
            values={{
              firstName: user?.first_name || "User",
            }}
          />
          <div className="ml-8 h-16 w-16 text-black">
            <ChevronDownSmall />
          </div>
        </button>

        {isDropdownVisible && (
          <div className="absolute right-0 z-50 mt-8 font-light text-black">
            <div className="w-216 rounded-8 bg-white py-10 shadow-minimal">
              <ul>
                <DropdownLink
                  href="/profile"
                  link_text={t("navigation.yourProfile") ?? ""}
                  setIsDropdownVisible={setIsDropdownVisible}
                />

                <DropdownLink
                  href="/favorites"
                  link_text={t("navigation.favorites") ?? ""}
                  setIsDropdownVisible={setIsDropdownVisible}
                />

                <DropdownLink
                  href="/create-recipe"
                  link_text={t("navigation.createRecipe") ?? ""}
                  setIsDropdownVisible={setIsDropdownVisible}
                />

                <DropdownLink
                  href="/account/settings"
                  link_text={t("navigation.accountSettings") ?? ""}
                  setIsDropdownVisible={setIsDropdownVisible}
                />

                <DropdownLink
                  is_logout
                  setIsDropdownVisible={setIsDropdownVisible}
                  link_text={t("navigation.logout") ?? ""}
                />
              </ul>
            </div>
          </div>
        )}
      </div>

      {isDropdownVisible && (
        <div className="fixed left-0 top-0 z-40 h-full w-full" onClick={() => setIsDropdownVisible(false)} />
      )}
    </>
  )
}

export default AccountActionDropdown
